<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<script>
import {
  ContourLabelToIcon,
  useFunctionCalculatorStore,
} from '@src/store/calculators/FunctionCalculatorStore';
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';

// import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { useSegmentationDialog } from '@/src/composables/useSegmentationDialog';

import { useContourContainerStore } from '../store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '../store/contours/contour-definitions';
import { useContourEventStore } from '../store/contours/contour-event-store';
import { useViewportStateStore } from '../store/contours/viewport-state-store';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';

export default defineComponent({
  name: 'SeriesTable',
  props: {
    viewportId: {
      type: String,
      required: true,
    },
    indexLVSystole: {
      type: Number,
      default: -1,
      required: false,
    },
    indexLVDiastole: {
      type: Number,
      default: -1,
      required: false,
    },
    indexRVSystole: {
      type: Number,
      default: -1,
      required: false,
    },
    indexRVDiastole: {
      type: Number,
      default: -1,
      required: false,
    },
    isSax: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  emits: [
    'cellSelected',
    'rowSelected',
    'columnSelected',
    'content-updated',
    'config-changed',
  ],
  setup(props, { emit }) {
    // Refs for DOM elements
    const columnHeadersContainer = ref(null);
    const rowHeadersContainer = ref(null);
    const gridContainer = ref(null);

    // Reactive state
    const scrollbarWidth = ref(0);
    const scrollbarHeight = ref(0);
    const showVerticalScrollbar = ref(false);
    const showHorizontalScrollbar = ref(false);
    const selectedRow = ref(null);
    const selectedColumn = ref(null);
    const selectedCell = ref({ row: 1, col: 1 }); // Default selection: top-left cell

    // stores
    const contourContainerStore = useContourContainerStore();
    const currentStudyStore = useCurrentStudyStore();
    const viewportStateStore = useViewportStateStore();
    const functionCalculatorStore = useFunctionCalculatorStore();

    // Composables
    // const backendSegmentation = useBackendSegmentation();

    const imageSelectionViewportStateStore = computed(() =>
      viewportStateStore.getSelectedImageIndices(props.viewportId),
    );

    const selectedSeriesIndex = computed(() => {
      return imageSelectionViewportStateStore.value.seriesIdx;
    });

    // rows - number of slices
    const numberOfSlices = computed(() => {
      if (
        typeof selectedSeriesIndex.value !== 'number'
        || selectedSeriesIndex.value < 0
      ) {
        return 0;
      }
      return currentStudyStore.getSliceCount(selectedSeriesIndex.value);
    });

    // columns - number of phases
    const maxNumPhases = computed(() => {
      if (
        typeof selectedSeriesIndex.value !== 'number'
        || selectedSeriesIndex.value < 0
      ) {
        return 0;
      }
      return currentStudyStore.getMaxPerSliceImageCount(
        selectedSeriesIndex.value,
      );
    });

    const localCellConfigs = ref([]);

    // Computed properties
    const cellWidth = computed(() => 40);
    const cellHeight = computed(() => 40);

    // Track if table content has been updated from store
    const tableContentUpdated = ref(false);

    // Context menu state
    const showContextMenu = ref(false);
    const contextMenuX = ref(0);
    const contextMenuY = ref(0);
    const contextMenuCell = ref({ row: 0, col: 0 });
    const virtualActivator = ref(null);

    // New context menu properties
    const contextMenuType = ref('cell'); // 'cell', 'row', or 'column'
    const contextMenuItems = ref({});

    // Computed title for the context menu
    const contextMenuTitle = computed(() => {
      switch (contextMenuType.value) {
        case 'table':
          return 'All Images';
        case 'row':
          return `Slice ${contextMenuCell.value.row}`;
        case 'column':
          return `Phase ${contextMenuCell.value.col}`;
        default:
          return `Image ${contextMenuCell.value.row},${contextMenuCell.value.col}`;
      }
    });

    // Add timer reference for auto-closing context menu
    const contextMenuTimer = ref(null);
    const contextMenuTimeout = 3000; // 3 seconds before auto-closing

    // Add these reactive variables
    const isMouseOverTable = ref(false);

    // Methods

    const onGridScroll = (event) => {
      if (columnHeadersContainer.value) {
        columnHeadersContainer.value.scrollLeft = event.target.scrollLeft;
      }

      if (rowHeadersContainer.value) {
        rowHeadersContainer.value.scrollTop = event.target.scrollTop;
      }
    };

    // Keep frozen headers aligned with the grid when scrollbars appear: the
    // grid's client area shrinks by the scrollbar size, so headers need matching
    // spacers or max scrollLeft/scrollTop ranges diverge and cells drift.
    const updateScrollbarMetrics = () => {
      const el = gridContainer.value;
      if (!el) return;
      const width = el.offsetWidth - el.clientWidth;
      const height = el.offsetHeight - el.clientHeight;
      scrollbarWidth.value = width;
      scrollbarHeight.value = height;
      showVerticalScrollbar.value = width > 0;
      showHorizontalScrollbar.value = height > 0;

      // Re-sync after spacer show/hide so scroll ranges stay matched.
      if (columnHeadersContainer.value) {
        columnHeadersContainer.value.scrollLeft = el.scrollLeft;
      }
      if (rowHeadersContainer.value) {
        rowHeadersContainer.value.scrollTop = el.scrollTop;
      }
    };

    let gridResizeObserver = null;

    // Helper to check if a cell is the currently selected one
    const isSelectedCell = (row, col) => {
      return (
        selectedCell.value
        && selectedCell.value.row === row
        && selectedCell.value.col === col
      );
    };

    // Function to ensure the selected cell is visible in the viewport
    const scrollToSelectedCell = () => {
      if (!selectedCell.value || !gridContainer.value) return;

      const { row, col } = selectedCell.value;

      // Calculate the position of the cell
      const cellTop = (row - 1) * cellHeight.value;
      const cellLeft = (col - 1) * cellWidth.value;
      const cellBottom = cellTop + cellHeight.value;
      const cellRight = cellLeft + cellWidth.value;

      // Get the current scroll position and viewport dimensions
      const { scrollTop } = gridContainer.value;
      const { scrollLeft } = gridContainer.value;
      const viewportHeight = gridContainer.value.clientHeight;
      const viewportWidth = gridContainer.value.clientWidth;
      const scrollBottom = scrollTop + viewportHeight;
      const scrollRight = scrollLeft + viewportWidth;

      // Determine if scrolling is needed
      if (cellTop < scrollTop) {
        // Cell is above the visible area
        gridContainer.value.scrollTop = cellTop;
      }
      else if (cellBottom > scrollBottom) {
        // Cell is below the visible area
        gridContainer.value.scrollTop = cellBottom - viewportHeight;
      }

      if (cellLeft < scrollLeft) {
        // Cell is to the left of the visible area
        gridContainer.value.scrollLeft = cellLeft;
      }
      else if (cellRight > scrollRight) {
        // Cell is to the right of the visible area
        gridContainer.value.scrollLeft = cellRight - viewportWidth;
      }
    };

    watch(imageSelectionViewportStateStore, (newImageIndices) => {
      selectedCell.value = {
        row: newImageIndices.sliceIdx + 1,
        col: newImageIndices.imageIdx + 1,
      };
      selectedRow.value = selectedCell.value.row;
      selectedColumn.value = selectedCell.value.col;
    });

    // Callback functions
    const onCellSelectionChanged = (cell) => {
      useViewportStateStore().setSelectedImageIndices(props.viewportId, {
        seriesIdx: selectedSeriesIndex.value,
        sliceIdx: cell.row - 1,
        imageIdx: cell.col - 1,
      });
    };

    // const onRowHeaderClicked = (rowIndex) => {
    //   // console.log('Row header clicked:', rowIndex);
    // };

    // const onColumnHeaderClicked = (colIndex) => {
    //   // console.log('Column header clicked:', colIndex);
    // };

    // Selection methods
    const selectCell = (row, col) => {
      if (isSelectedCell(row, col)) {
        return;
      }

      selectedCell.value = { row, col };
      selectedRow.value = row;
      selectedColumn.value = col;

      emit('cellSelected', selectedCell.value);
      emit('rowSelected', selectedRow.value);
      emit('columnSelected', selectedColumn.value);

      // Call the callback for cell selection change
      onCellSelectionChanged(selectedCell.value);

      // Ensure the selected cell is visible
      scrollToSelectedCell();
    };

    const selectRow = (rowIndex) => {
      // Call the row header clicked callback
      // onRowHeaderClicked(rowIndex);

      if (selectedRow.value === rowIndex) {
        if (selectedCell.value) {
          return;
        }
        selectedRow.value = null;
      }
      else {
        selectedRow.value = rowIndex;

        if (selectedCell.value) {
          selectedCell.value = {
            row: rowIndex,
            col: selectedCell.value.col,
          };

          emit('cellSelected', selectedCell.value);
          onCellSelectionChanged(selectedCell.value);
        }
      }

      emit('rowSelected', selectedRow.value);

      // Ensure the selected cell is visible
      if (selectedCell.value) {
        scrollToSelectedCell();
      }
    };

    const selectColumn = (colIndex) => {
      // Call the column header clicked callback
      // onColumnHeaderClicked(colIndex);

      if (selectedColumn.value === colIndex) {
        if (selectedCell.value) {
          return;
        }
        selectedColumn.value = null;
      }
      else {
        selectedColumn.value = colIndex;

        if (selectedCell.value) {
          selectedCell.value = {
            row: selectedCell.value.row,
            col: colIndex,
          };

          emit('cellSelected', selectedCell.value);
          onCellSelectionChanged(selectedCell.value);
        }
      }

      emit('columnSelected', selectedColumn.value);

      // Ensure the selected cell is visible
      if (selectedCell.value) {
        scrollToSelectedCell();
      }
    };

    const clearSelection = () => {
      selectedCell.value = { row: 1, col: 1 };
      selectedRow.value = 1;
      selectedColumn.value = 1;

      emit('cellSelected', selectedCell.value);
      emit('rowSelected', selectedRow.value);
      emit('columnSelected', selectedColumn.value);

      onCellSelectionChanged(selectedCell.value);
    };

    // Setter for cell configuration
    const setCellConfig = (imageUID, config) => {
      localCellConfigs.value[imageUID] = config;
    };

    const columnRowToImageUID = (row, col) => {
      if (
        typeof selectedSeriesIndex.value !== 'number'
        || selectedSeriesIndex.value < 0
      ) {
        return null;
      }
      const sliceImageUIDs = currentStudyStore.getSliceImageUIDs(
        selectedSeriesIndex.value,
        row - 1,
      );
      return sliceImageUIDs && sliceImageUIDs[col - 1]
        ? sliceImageUIDs[col - 1]
        : null;
    };

    const excludedUids = computed(() => functionCalculatorStore.excludedImageUidsSet);
    const isExcludedCell = (row, col) => {
      const uid = columnRowToImageUID(row, col);
      return !!uid && excludedUids.value.has(uid);
    };

    // Enhanced getter for cell configuration
    const getCellConfig = (row, col) => {
      const imageUID = columnRowToImageUID(row, col);
      // Check local cell configs
      const configFromLocal = localCellConfigs.value[imageUID];
      if (configFromLocal) {
        return configFromLocal;
      }

      // Default empty config
      return [];
    };

    const getIconsFromRowCol = (row, col) => {
      const config = getCellConfig(row, col);
      return config.map(contour => ContourLabelToIcon[contour]);
    };

    // Function to update the table based on store data
    const updateTableCells = () => {
      if (selectedSeriesIndex.value >= 0) {
        for (let r = 0; r < numberOfSlices.value; r++) {
          const sliceImageUIDs = currentStudyStore.getSliceImageUIDs(
            selectedSeriesIndex.value,
            r,
          );
          if (sliceImageUIDs) {
            for (let c = 0; c < sliceImageUIDs.length; c++) {
              const imageUID = sliceImageUIDs[c];
              const activeContors = [];

              for (const key of Object.keys(ContourLabelToIcon)) {
                const isPresent = contourContainerStore.isPresentContourLabel(
                  imageUID,
                  ContourLabel[key],
                );
                if (isPresent) {
                  activeContors.push(ContourLabel[key]);
                }
              }
              setCellConfig(imageUID, activeContors);
            }
          }
        }
      }
    };

    // contour events
    const unsub = useContourEventStore().on(() => {
      updateTableCells();
    });
    onBeforeUnmount(unsub);

    watch(selectedSeriesIndex, () => {
      updateTableCells();
    }, { immediate: true });

    // Modified hideContextMenu method to clear the timer
    const hideContextMenu = () => {
      showContextMenu.value = false;

      // Clear the timer when hiding menu
      if (contextMenuTimer.value) {
        clearTimeout(contextMenuTimer.value);
        contextMenuTimer.value = null;
      }
    };

    // Helper method to start auto-close timer
    const startAutoCloseTimer = () => {
      // Clear any existing timer first
      if (contextMenuTimer.value) {
        clearTimeout(contextMenuTimer.value);
      }

      // Set new timer
      contextMenuTimer.value = setTimeout(() => {
        hideContextMenu();
      }, contextMenuTimeout);
    };

    // Get items to show in the cell context menu (only true values)
    const getContextMenuItemsForCell = (row, col) => {
      const config = getCellConfig(row, col);
      const menuItems = {};

      // Only include properties that are true
      config.forEach((contour) => {
        menuItems[contour] = true;
      });

      return menuItems;
    };

    // Get items that are true in at least one cell in the row
    const getContextMenuItemsForRow = (rowIndex) => {
      const menuItems = {};

      // Check all cells in this row
      for (let col = 1; col <= maxNumPhases.value; col++) {
        const config = getCellConfig(rowIndex, col);

        // Add any property that is true
        config.forEach((contour) => {
          menuItems[contour] = true;
        });
      }

      return menuItems;
    };

    // Get items that are true in at least one cell in the column
    const getContextMenuItemsForColumn = (colIndex) => {
      const menuItems = {};

      // Check all cells in this column
      for (let row = 1; row <= numberOfSlices.value; row++) {
        const config = getCellConfig(row, colIndex);

        // Add any property that is true
        config.forEach((contour) => {
          menuItems[contour] = true;
        });
      }

      return menuItems;
    };

    // Get items that are true in at least one cell in the entire table
    const getContextMenuItemsForTable = () => {
      const menuItems = {};

      // Check all cells in the table
      for (let row = 1; row <= numberOfSlices.value; row++) {
        for (let col = 1; col <= maxNumPhases.value; col++) {
          const config = getCellConfig(row, col);

          // Add any property that is true
          config.forEach((contour) => {
            menuItems[contour] = true;
          });
        }
      }

      return menuItems;
    };

    // Helper method to position and show context menu
    const showAndPositionMenu = (event, type, cell, items) => {
      event.preventDefault();

      contextMenuCell.value = cell;
      contextMenuType.value = type;
      contextMenuItems.value = items;

      contextMenuX.value = event.clientX;
      contextMenuY.value = event.clientY;
      showContextMenu.value = true;

      // Start auto-close timer
      startAutoCloseTimer();
    };

    // Update all context menu display methods to use the positioning helper
    const onContextMenu = (event, row, col) => {
      showAndPositionMenu(
        event,
        'cell',
        { row, col },
        getContextMenuItemsForCell(row, col),
      );
    };

    const onRowHeaderContextMenu = (event, rowIndex) => {
      showAndPositionMenu(
        event,
        'row',
        { row: rowIndex, col: 1 },
        getContextMenuItemsForRow(rowIndex),
      );
    };

    const onColumnHeaderContextMenu = (event, colIndex) => {
      showAndPositionMenu(
        event,
        'column',
        { row: 1, col: colIndex },
        getContextMenuItemsForColumn(colIndex),
      );
    };

    const onCornerCellContextMenu = (event) => {
      showAndPositionMenu(
        event,
        'table',
        { row: 1, col: 1 },
        getContextMenuItemsForTable(),
      );
    };

    // Empty callbacks for different context menu types
    const onCellContextMenuAction = (
      action,
      targetLabel = null,
      imageUIDs = [],
    ) => {
      if (action === 'delete') {
        // Handle delete action
        const contourId = {
          label: targetLabel,
          id: encodeContourLabelItemId([0]),
        };

        contourContainerStore.bulkDeleteContour(imageUIDs, contourId);
      }
      else if (action === 'segment') {
        const segDialog = useSegmentationDialog();
        // const targetEndoLabel = props.isSax
        //   ? ContourLabel.SaxLvEndo
        //   : ContourLabel.LaxLvEndo;
        // let targetSlices = [];
        // if (contextMenuType.value === 'row') {
        //   targetSlices = [contextMenuCell.value.row - 1];
        // } else if (contextMenuType.value === 'table') {
        //   const sliceCount = currentStudyStore.getSliceCount(
        //     selectedSeriesIndex.value
        //   );
        //   targetSlices = Array.from({ length: sliceCount }, (_, i) => i);
        // }
        segDialog.trigger([
          {
            viewerID: props.viewportId,
            imageUIDs,
          },
        ]);

        // backendSegmentation.segmentFunctionModule(
        //   {
        //     include: imageUIDs,
        //   },
        //   selectedSeriesIndex.value,
        //   targetSlices,
        //   targetEndoLabel
        // );
      }
      else if (action === 'deleteAll') {
        contourContainerStore.bulkDeleteAllContours(imageUIDs);
      }
    };

    const getImageUIDsFromContext = () => {
      switch (contextMenuType.value) {
        case 'table':
          return currentStudyStore.getSeriesImageUIDs(
            selectedSeriesIndex.value,
          );
        case 'row':
          return currentStudyStore.getSliceImageUIDs(
            selectedSeriesIndex.value,
            contextMenuCell.value.row - 1,
          );
        case 'column':
          return currentStudyStore.getPhaseImageUIDs(
            selectedSeriesIndex.value,
            contextMenuCell.value.col - 1,
          );
        default: {
          const imageUID = currentStudyStore.getImageUID({
            seriesIdx: selectedSeriesIndex.value,
            sliceIdx: contextMenuCell.value.row - 1,
            imageIdx: contextMenuCell.value.col - 1,
          });
          // Ensure we return an array
          return imageUID ? [imageUID] : [];
        }
      }
    };

    const onTableContextMenuAction = (action) => {
      const contourId = {
        label: action,
        id: encodeContourLabelItemId([0]),
      };
      const imageUIDs = currentStudyStore.getSeriesImageUIDs(
        selectedSeriesIndex.value,
      );
      contourContainerStore.bulkDeleteContour(imageUIDs, contourId);
    };

    // Enhanced context menu action handler
    const handleContextMenuAction = (actionKey, targetLabel = null) => {
      const imageUIDs = getImageUIDsFromContext();
      if (!imageUIDs || imageUIDs.length === 0) {
        console.warn('No image UIDs found for context menu action');
        hideContextMenu();

        return;
      }
      onCellContextMenuAction(actionKey, targetLabel, imageUIDs);

      hideContextMenu();
    };

    // Format menu item labels to be more readable
    const formatMenuItemLabel = (key) => {
      let label = '';
      if (key === 'greenCircle') {
        label = 'Delete SAX Epi';
      }
      else if (key === 'greenOpenCircle') {
        label = 'Delete SAX Open Epi';
      }
      else if (key === 'redCircle') {
        label = 'Delete SAX Endo';
      }
      else if (key === 'blueCircle') {
        label = 'Delete RV Insertion';
      }
      else if (key === 'yellowCircle') {
        label = 'Delete RV Endo';
      }
      else if (key === 'magentaDot') {
        label = 'Delete SAX Papillary';
      }
      else if (key === 'blueDot') {
        label = 'Delete SAX Exclusion';
      }
      else if (key === 'greenU') {
        label = 'Delete LAX Epi';
      }
      else if (key === 'redU') {
        label = 'Delete LAX Endo';
      }

      return label;
    };

    // Update the handleKeyDown function to check mouse position
    const handleKeyDown = (event) => {
      // Only process keyboard events when mouse is over the table
      if (!isMouseOverTable.value) return;

      if (!selectedCell.value) return;

      const { row, col } = selectedCell.value;
      let newRow = row;
      let newCol = col;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          newRow = Math.max(1, row - 1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          newRow = Math.min(numberOfSlices.value, row + 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          newCol = Math.max(1, col - 1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          newCol = Math.min(maxNumPhases.value, col + 1);
          break;
        default:
          return; // Exit if not an arrow key
      }

      // Only update if the cell would actually change
      if (newRow !== row || newCol !== col) {
        selectCell(newRow, newCol);
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      // Set initial selected row and column based on default cell
      selectedRow.value = selectedCell.value.row;
      selectedColumn.value = selectedCell.value.col;

      // Emit the initial selection
      emit('cellSelected', selectedCell.value);
      emit('rowSelected', selectedRow.value);
      emit('columnSelected', selectedColumn.value);

      // Add escape key handler to close the context menu
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && showContextMenu.value) {
          hideContextMenu();
        }
      });

      // updateTableCells(); // This is causing recursion, so we call it in the watcher instead

      // Focus the table container to enable keyboard navigation
      if (document.querySelector('.series-table-container')) {
        document.querySelector('.series-table-container').focus();
      }

      nextTick(() => {
        updateScrollbarMetrics();
        if (gridContainer.value && typeof ResizeObserver !== 'undefined') {
          gridResizeObserver = new ResizeObserver(() => {
            updateScrollbarMetrics();
          });
          gridResizeObserver.observe(gridContainer.value);
        }
      });
    });

    onBeforeUnmount(() => {
      // Clear any active context menu timer
      if (contextMenuTimer.value) {
        clearTimeout(contextMenuTimer.value);
      }

      // Remove event listeners for mouse enter and leave
      window.removeEventListener('keydown', handleKeyDown);

      if (gridResizeObserver) {
        gridResizeObserver.disconnect();
        gridResizeObserver = null;
      }
    });

    // Watch for prop changes
    // Also scroll to selected cell when store updates the selection
    watch(
      () => selectedCell.value,
      (newSelectedCell) => {
        if (newSelectedCell) {
          scrollToSelectedCell();
        }
      },
    );

    // Content size changes can add/remove scrollbars — remeasure spacers.
    watch([numberOfSlices, maxNumPhases], () => {
      nextTick(updateScrollbarMetrics);
    });

    // Expose to template
    return {
      // Refs
      columnHeadersContainer,
      rowHeadersContainer,
      gridContainer,
      virtualActivator,

      // State
      scrollbarWidth,
      scrollbarHeight,
      showVerticalScrollbar,
      showHorizontalScrollbar,
      selectedRow,
      selectedColumn,
      selectedCell,

      // Computed
      cellWidth,
      cellHeight,
      numberOfSlices,
      maxNumPhases,

      // Methods
      onGridScroll,
      isSelectedCell,
      isExcludedCell,
      selectCell,
      selectRow,
      selectColumn,
      clearSelection,
      getCellConfig,
      getIconsFromRowCol,
      tableContentUpdated,
      showContextMenu,
      contextMenuX,
      contextMenuY,
      contextMenuCell,
      onContextMenu,
      hideContextMenu,
      contextMenuTitle,
      contextMenuItems,
      onRowHeaderContextMenu,
      onColumnHeaderContextMenu,
      onCellContextMenuAction,
      formatMenuItemLabel,
      onCornerCellContextMenu,
      onTableContextMenuAction,
      handleContextMenuAction,
      scrollToSelectedCell,
      handleKeyDown,
      isMouseOverTable,
    };
  },
});
</script>

<template>
  <div
    class="series-table-container"
    tabindex="0"
    @mouseenter="isMouseOverTable = true"
    @mouseleave="isMouseOverTable = false"
    @keydown="handleKeyDown"
  >
    <!-- Column headers row with corner -->
    <div class="header-row">
      <div
        class="corner-cell"
        @click="clearSelection"
        @contextmenu="onCornerCellContextMenu($event)"
      />
      <div ref="columnHeadersContainer" class="column-headers-container">
        <div class="column-headers-inner">
          <div
            v-for="colIndex in maxNumPhases"
            :key="`col-${colIndex}`"
            class="column-header"
            :class="{ selected: selectedColumn === colIndex }"
            @click="selectColumn(colIndex)"
            @contextmenu="onColumnHeaderContextMenu($event, colIndex)"
          >
            {{ colIndex }}
            <!-- D indicators on left, safe for undefined/null values -->
            <span
              v-if="indexLVDiastole != null && colIndex === indexLVDiastole + 1"
              class="header-indicator lv-diastole"
            >D</span>
            <span
              v-if="indexRVDiastole != null && colIndex === indexRVDiastole + 1"
              class="header-indicator rv-diastole"
            >D</span>
            <!-- S indicators on right, safe for undefined/null values -->
            <span
              v-if="indexLVSystole != null && colIndex === indexLVSystole + 1"
              class="header-indicator lv-systole"
            >S</span>
            <span
              v-if="indexRVSystole != null && colIndex === indexRVSystole + 1"
              class="header-indicator rv-systole"
            >S</span>
          </div>
        </div>
      </div>
      <!-- Spacer to account for vertical scrollbar - only visible when vertical scrollbar is present -->
      <div
        v-if="showVerticalScrollbar"
        class="scrollbar-spacer"
        :style="{ width: `${scrollbarWidth}px` }"
      />
    </div>

    <!-- Main content area with row headers and scrollable grid -->
    <div class="table-body">
      <div class="row-headers-column">
        <div ref="rowHeadersContainer" class="row-headers-container">
          <div class="row-headers-inner">
            <div
              v-for="rowIndex in numberOfSlices"
              :key="`row-${rowIndex}`"
              class="row-header"
              :class="{ selected: selectedRow === rowIndex }"
              @click="selectRow(rowIndex)"
              @contextmenu="onRowHeaderContextMenu($event, rowIndex)"
            >
              {{ rowIndex }}
            </div>
          </div>
        </div>
        <!-- Spacer for the grid's horizontal scrollbar so vertical scroll stays aligned -->
        <div
          v-if="showHorizontalScrollbar"
          class="row-headers-scrollbar-spacer"
          :style="{ height: `${scrollbarHeight}px` }"
        />
      </div>

      <!-- This is the only area that should scroll -->
      <div ref="gridContainer" class="grid-container" @scroll="onGridScroll">
        <div class="grid-inner">
          <div
            v-for="rowIndex in numberOfSlices"
            :key="`row-${rowIndex}`"
            class="grid-row"
          >
            <div
              v-for="colIndex in maxNumPhases"
              :key="`cell-${rowIndex}-${colIndex}`"
              class="grid-cell"
              :class="{
                'in-selected-row': selectedRow === rowIndex,
                'in-selected-column': selectedColumn === colIndex,
                'selected-cell': isSelectedCell(rowIndex, colIndex),
                'excluded-cell': isExcludedCell(rowIndex, colIndex),
              }"
              @click="selectCell(rowIndex, colIndex)"
              @contextmenu="onContextMenu($event, rowIndex, colIndex)"
            >
              <div class="square-icon">
                <div
                  v-for="(icon, index) in getIconsFromRowCol(
                    rowIndex,
                    colIndex,
                  )"
                  :key="`icon-${index}`"
                  :class="icon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Virtual activator element positioned at click coordinates -->
  <div
    ref="virtualActivator"
    :style="{
      position: 'fixed',
      left: `${contextMenuX}px`,
      top: `${contextMenuY}px`,
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
    }"
  />

  <!-- Add context menu component using v-menu -->
  <v-menu
    v-model="showContextMenu"
    class="custom-context-menu"
    :activator="virtualActivator"
    close-on-content-click
    location="bottom start"
    origin="auto"
    :offset="5"
    :close-on-back="true"
    :max-height="430"
    :max-width="300"
    transition="slide-y-transition"
    :flip="true"
    :shift="true"
  >
    <v-card class="context-menu-card">
      <v-card-title class="context-menu-title">
        {{ contextMenuTitle }}
      </v-card-title>
      <v-divider />

      <v-list density="compact">
        <v-list-item
          v-for="(value, key) in contextMenuItems"
          :key="key"
          class="context-menu-item"
          @click="handleContextMenuAction('delete', key)"
        >
          <template #prepend>
            <v-icon size="small" class="mr-2">
              mdi-delete-outline
            </v-icon>
          </template>
          Delete {{ key }}
        </v-list-item>

        <v-list-item
          class="context-menu-item"
          @click="handleContextMenuAction('segment')"
        >
          <template #prepend>
            <v-icon size="small" class="mr-2">
              mdi-image-filter-center-focus
            </v-icon>
          </template>
          Segment ({{ contextMenuTitle }})
        </v-list-item>

        <v-list-item
          v-if="Object.keys(contextMenuItems).length > 0"
          class="context-menu-item"
          @click="handleContextMenuAction('deleteAll')"
        >
          <template #prepend>
            <v-icon size="small" class="mr-2">
              mdi-delete-sweep-outline
            </v-icon>
          </template>
          Delete All ({{ contextMenuTitle }})
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.series-table-container {
  position: relative;
  border: 1px solid var(--md-surface-container-highest);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--md-surface);
  display: flex;
  flex-direction: column;
  outline: none; /* Remove default focus outline */
}

.series-table-container:focus {
  border-color: var(--md-warning);
}

/* Header row with corner cell and column headers */
.header-row {
  display: flex;
  width: 100%;
  z-index: 3;
  flex-shrink: 0;
  background-color: var(--md-surface-container-high);
  border-bottom: 1px solid var(--md-surface-container-highest);
}

.corner-cell {
  width: 28px;
  min-width: 28px;
  height: 28px;
  background-color: var(--md-surface);
  border-right: 1px solid var(--md-surface-container-highest);
  border-bottom: 1px solid var(--md-surface-container-highest);
  flex-shrink: 0;
  cursor: pointer;
  border-top-left-radius: 6px;
}

.corner-cell:hover {
  background-color: var(--md-surface);
}

.column-headers-container {
  overflow: auto;
  background-color: var(--md-surface);
  flex: 1;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
  border-top-right-radius: 6px;
}

.column-headers-container::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}

.column-headers-inner {
  display: flex;
  flex-shrink: 0;
  border-bottom: 1px solid var(--md-surface-container-highest);
}

.column-header {
  width: 40px;
  min-width: 40px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--md-surface-container-highest);
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
  transition: all 0.15s ease;
  cursor: pointer;
  position: relative; /* Ensure position relative for absolute positioned indicators */
}

.column-header:hover {
  background-color: var(--md-surface-container-high);
}

.column-header.selected {
  background-color: var(--md-surface-container-high);
}

.scrollbar-spacer {
  height: 28px;
  background-color: var(--md-surface);
  border-bottom: 1px solid var(--md-surface-container-highest);
  flex-shrink: 0;
}

/* Main body area with row headers and grid */
.table-body {
  display: flex;
  flex: 1;
  width: 100%;
  overflow: hidden;
  margin: 0;
  min-height: 0;
}

.row-headers-column {
  display: flex;
  flex-direction: column;
  width: 28px;
  min-width: 28px;
  flex-shrink: 0;
  min-height: 0;
}

.row-headers-container {
  width: 28px;
  min-width: 28px;
  overflow: auto;
  background-color: var(--md-surface);
  z-index: 2;
  flex: 1;
  min-height: 0;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
  border-right: 1px solid var(--md-surface-container-highest);
  border-bottom-left-radius: 6px;
}

.row-headers-scrollbar-spacer {
  width: 28px;
  min-width: 28px;
  flex-shrink: 0;
  background-color: var(--md-surface);
  border-right: 1px solid var(--md-surface-container-highest);
}

.row-headers-container::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}

.row-headers-inner {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.row-header {
  width: 28px;
  min-width: 28px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--md-surface-container-highest);
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
  transition: all 0.15s ease;
  cursor: pointer;
}

.row-header:hover {
  background-color: var(--md-surface-container-high);
}

.row-header.selected {
  background-color: var(--md-surface);
}

/* Scrollable grid area */
.grid-container {
  flex: 1;
  overflow: auto;
  background-color: var(--md-surface);
  position: relative;
  outline: none;
  box-sizing: border-box;
}

.grid-inner {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.grid-row {
  display: flex;
  flex-shrink: 0;
}

.grid-cell {
  width: 40px;
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--md-surface-container-highest);
  border-bottom: 1px solid var(--md-surface-container-highest);
  flex-shrink: 0;
  transition: background-color 0.15s ease;
  position: relative;
  cursor: pointer;
  border-radius: 2px;
}

.grid-cell:hover {
  background-color: var(--md-surface-container-high);
}

/* Selected row and column styles */
.grid-cell.in-selected-row {
  background-color: rgba(255, 255, 255, 0.05);
}

.grid-cell.in-selected-column {
  background-color: rgba(255, 255, 255, 0.05);
}

.grid-cell.selected-cell {
  background-color: rgba(255, 154, 60, 0.15);
  z-index: 1;
  box-shadow: none;
  position: relative;
}

/* Create rounded outline for selected cell */
.grid-cell.selected-cell::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid var(--md-warning);
  border-radius: 8px;
  pointer-events: none;
  z-index: 2;
}

/* Update border styles for row/column selections to have rounded corners at ends */
.grid-cell.in-selected-row:first-child {
  border-left: 2px solid var(--md-warning);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  box-shadow: none;
}

.grid-cell.in-selected-row:last-child {
  border-right: 2px solid var(--md-warning);
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: none;
}

.grid-row:first-child .grid-cell.in-selected-column {
  border-top: 2px solid var(--md-warning);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  box-shadow: none;
}

.grid-row:last-child .grid-cell.in-selected-column {
  border-bottom: 2px solid var(--md-warning);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: none;
}

.square-icon {
  position: relative;
  width: 35px;
  height: 35px;
  background-color: var(--md-surface-container-high);
  border-radius: 6px;
  transition: all 0.2s ease;
  overflow: visible;
}

.grid-cell:hover .square-icon {
  background-color: var(--md-surface);
  transform: scale(1.05);
}

/* Only adding rounded corners to specific elements */

/* Round the corner cell */
.corner-cell {
  border-top-left-radius: 6px;
}

/* Round the header containers */
.column-headers-container {
  border-top-right-radius: 6px;
}

.row-headers-container {
  border-bottom-left-radius: 6px;
}

/* Round the first and last column headers */
.column-headers-inner > .column-header:first-child {
  border-top-left-radius: 4px;
}

.column-headers-inner > .column-header:last-child {
  border-top-right-radius: 4px;
}

/* Round the first and last row headers */
.row-headers-inner > .row-header:first-child {
  border-top-left-radius: 4px;
}

.row-headers-inner > .row-header:last-child {
  border-bottom-left-radius: 4px;
}

/* Add subtle rounding to all grid cells */
.grid-cell {
  border-radius: 2px;
}

/* Round the square icons */
.square-icon {
  border-radius: 6px;
}

/* Round the selected cell highlight with increased radius */
.grid-cell.selected-cell::after {
  border-radius: 8px;
}

.grid-cell.excluded-cell {
  opacity: 0.35;
  filter: grayscale(1);
}

/* Add new styles for the circles */
.green-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  background-color: transparent;
  border: 2px solid #48c774;
  border-radius: 50%;
}

.green-open-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  background-color: transparent;
  border: 2px solid #48c774;
  border-top-color: transparent; /* quarter of circumference open at top */
  border-radius: 50%;
}

.red-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background-color: transparent;
  border: 2px solid #ff3860;
  border-radius: 50%;
}

.blue-circle {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 12px;
  height: 12px;
  background-color: #3273dc;
  border-radius: 50%;
  transform: none;
}

.yellow-circle {
  position: absolute;
  bottom: 3px;
  left: 3px;
  width: 12px;
  height: 12px;
  background-color: #ffdd57;
  border-radius: 50%;
  transform: none;
}

/* Make the U shapes have the same distance between vertical lines as circles */
.green-u {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(180deg);
  width: 30px;
  height: 28px;
  background-color: transparent;
  border: 2px solid #48c774;
  border-radius: 15px 15px 0 0;
  border-bottom: none;
}

/* Make red U's curved part not overlap with green U */
.red-u {
  position: absolute;
  top: 42%;
  /* Move slightly lower than green U */
  left: 50%;
  transform: translate(-50%, -50%) rotate(180deg);
  width: 16px;
  height: 20px;
  /* Slightly shorter to prevent curve overlap */
  background-color: transparent;
  border: 2px solid #ff3860;
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  z-index: 1;
}

/* Presence dots shown inside the red (SAX Endo) circle */
.magenta-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(calc(-50% - 3px), -50%);
  width: 5px;
  height: 5px;
  background-color: magenta;
  border-radius: 50%;
  z-index: 1;
}

.blue-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(calc(-50% + 3px), -50%);
  width: 5px;
  height: 5px;
  background-color: #3273dc; /* blue */
  border-radius: 50%;
  z-index: 1;
}

/* Context Menu Styles */
.custom-context-menu {
  z-index: 999999;
}

.context-menu-card {
  background-color: var(--md-bg, #333333);
  color: var(--md-text-primary, #e0e0e0);
  display: flex;
  flex-direction: column;
}

.context-menu-title {
  font-size: 14px;
  background-color: var(--md-bg, #444444);
  padding: 8px 16px;
}

.context-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 13px;
  padding: 8px 16px;
  min-height: 0px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Add styles to properly handle scrollbars */
.grid-container {
  overflow: auto; /* Ensure scrollbars appear when needed */
  flex: 1;
}

/* Add a container for the horizontal scrollbar if needed */
.scrollbar-container {
  height: 15px; /* Height of scrollbar */
  display: flex;
  overflow: hidden;
  background-color: #444444;
}

/* Ensure the scrollbar-spacer at the bottom matches the one at the top */
.bottom-scrollbar-spacer {
  width: 28px; /* Same as row header width */
  min-width: 28px;
  flex-shrink: 0;
}

/* Add styles for the header indicators */
.header-indicator {
  position: absolute;
  font-size: 8px;
  font-weight: bold;
  bottom: 2px;
}

/* Position D indicators on left */
.lv-diastole {
  color: #ff3860; /* Red color for LV */
  left: 2px;
}

.rv-diastole {
  color: #ffdd57; /* Yellow color for RV */
  left: 10px; /* Positioned to the right of the LV diastole indicator */
}

/* Position S indicators on right */
.lv-systole {
  color: #ff3860; /* Red color for LV */
  right: 10px;
}

.rv-systole {
  color: #ffdd57; /* Yellow color for RV */
  right: 2px;
}
</style>
